import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Save, RotateCcw, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { fmtDateTime } from "@/lib/format";

/**
 * PromptEditor — Pannello collassabile per visualizzare e modificare
 * un system prompt AI. Usa /api/admin/prompts (solo admin).
 *
 * Props:
 *   - promptKey: string (es. "news_editor", "monthly_summary", "briefing_technical", ...)
 *   - defaultOpen: bool (default false)
 */
export default function PromptEditor({ promptKey, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: all } = await api.get("/admin/prompts");
      const item = all.find((p) => p.key === promptKey);
      if (item) {
        setData(item);
        setDraft(item.system_message || "");
      }
    } catch {
      // Non-admin users don't see this component; silent fail
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // Reset dello state e ricarica quando cambia la chiave del prompt
    // (es. switch technical/simplified nel Briefing)
    setData(null);
    setDraft("");
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptKey]);

  useEffect(() => {
    if (open && !data && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async () => {
    if (!draft.trim()) {
      toast.error("Il prompt non può essere vuoto");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/admin/prompts/${promptKey}`, { system_message: draft });
      toast.success("Prompt aggiornato");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/prompts/${promptKey}/reset`);
      toast.success("Prompt riportato al default");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore reset");
    } finally {
      setBusy(false);
    }
  };

  const isDirty = data && draft !== (data.system_message || "");

  return (
    <div className="card-elevated overflow-hidden" data-testid={`prompt-editor-${promptKey}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3 border-b border-slate-100 flex items-center justify-between hover:bg-slate-50/60 transition-colors text-left"
        data-testid={`prompt-editor-toggle-${promptKey}`}
      >
        <span className="flex items-center gap-2 font-display font-semibold text-slate-800">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Sparkles size={14} className="text-purple-500" />
          Prompt AI di base
          {data?.is_custom && (
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
              Personalizzato
            </span>
          )}
        </span>
        {data?.updated_at && (
          <span className="text-xs text-slate-400">
            Modificato: {fmtDateTime(data.updated_at)}
          </span>
        )}
      </button>
      {open && (
        <div className="p-5 space-y-3">
          {loading || !data ? (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Caricamento…
            </div>
          ) : (
            <>
              <div className="text-xs text-slate-500">
                <span className="font-semibold text-slate-700">{data.label}</span> — {data.description}
              </div>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={14}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 outline-none text-xs leading-relaxed font-mono bg-slate-50/60"
                data-testid={`prompt-editor-textarea-${promptKey}`}
              />
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={save}
                  disabled={busy || !isDirty}
                  className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1.5 font-semibold flex items-center gap-1.5 disabled:opacity-40"
                  data-testid={`prompt-editor-save-${promptKey}`}
                >
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  Salva prompt
                </button>
                {data.is_custom && (
                  <button
                    onClick={reset}
                    disabled={busy}
                    className="text-sm border border-slate-200 hover:border-amber-400 hover:text-amber-600 bg-white rounded-lg px-3 py-1.5 font-semibold flex items-center gap-1.5 disabled:opacity-40"
                    data-testid={`prompt-editor-reset-${promptKey}`}
                  >
                    <RotateCcw size={13} /> Ripristina default
                  </button>
                )}
                {isDirty && (
                  <span className="text-xs text-amber-600 font-medium">Modifiche non salvate</span>
                )}
              </div>
              <p className="text-[11px] text-slate-400 italic">
                Nota: puoi personalizzare il prompt in qualsiasi momento. Se il prompt originale
                contiene segnaposto tipo <code className="text-slate-500">{"{today}"}</code> o{" "}
                <code className="text-slate-500">{"{max_per_category}"}</code>, mantienili nel testo per far
                funzionare correttamente la sostituzione dei valori.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
